import Phaser from "phaser";
import { COLORS, GAME_HEIGHT, GAME_WIDTH } from "../config";
import { AudioFx } from "../systems/AudioFx";
import { SaveState } from "../systems/SaveState";

const CHECK_IN_OPTIONS = ["Scratchy throat", "A bit tired", "Just fine"];

/**
 * Home screen: stylized body map (Lungs is the only active region this slice),
 * points total, first-launch disclaimer, and the daily check-in. Tapping the
 * glowing Lungs region starts a mission.
 */
export default class MenuScene extends Phaser.Scene {
  private pointsText!: Phaser.GameObjects.Text;

  constructor() {
    super("Menu");
  }

  create(): void {
    this.drawBackground();

    this.add
      .text(GAME_WIDTH / 2, 120, "Immune Defender", {
        fontFamily: "Arial, sans-serif",
        fontSize: "52px",
        color: "#eaf6ff",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    this.add
      .text(GAME_WIDTH / 2, 172, "Breathe. Defend. Heal.", {
        fontFamily: "Arial, sans-serif",
        fontSize: "24px",
        color: "#9fd8ff",
      })
      .setOrigin(0.5);

    this.drawBodyMap();

    this.pointsText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 70, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "28px",
        color: "#bdf0d0",
      })
      .setOrigin(0.5);
    this.refreshPoints();

    // Unlock audio on the first interaction anywhere.
    this.input.once(Phaser.Input.Events.POINTER_DOWN, () => AudioFx.unlock());

    if (SaveState.consumeDisclaimer()) {
      this.showDisclaimer();
    } else {
      this.maybeShowCheckIn();
    }
  }

  private refreshPoints(): void {
    this.pointsText.setText(`Vitality points: ${SaveState.points}`);
  }

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillGradientStyle(COLORS.bgTop, COLORS.bgTop, COLORS.bgBottom, COLORS.bgBottom, 1);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  /** Simple body silhouette with a pulsing, tappable inflamed Lungs region. */
  private drawBodyMap(): void {
    const cx = GAME_WIDTH / 2;
    const top = 300;

    const body = this.add.graphics();
    body.fillStyle(0xffffff, 0.06);
    body.lineStyle(3, 0x9fd8ff, 0.4);
    // Head
    body.fillCircle(cx, top + 40, 46);
    body.strokeCircle(cx, top + 40, 46);
    // Torso
    body.fillRoundedRect(cx - 110, top + 92, 220, 300, 60);
    body.strokeRoundedRect(cx - 110, top + 92, 220, 300, 60);
    // Arms
    body.fillRoundedRect(cx - 168, top + 110, 50, 210, 25);
    body.fillRoundedRect(cx + 118, top + 110, 50, 210, 25);

    // Lungs region (upper chest) — two lobes.
    const lungsY = top + 168;
    this.pulseGlow(cx, lungsY, COLORS.inflamed);
    const lungs = this.add.graphics();
    lungs.fillStyle(COLORS.inflamed, 0.85);
    lungs.fillEllipse(cx - 32, lungsY, 50, 80);
    lungs.fillEllipse(cx + 32, lungsY, 50, 80);
    this.regionLabel(cx, lungsY, "Lungs");
    this.hotspotZone(cx, lungsY, 200, 150, "lungs");

    // Intestines region (lower abdomen) — a coil of loops.
    const gutY = top + 322;
    const gutColor = 0xffa15c;
    this.pulseGlow(cx, gutY, gutColor);
    const gut = this.add.graphics();
    gut.fillStyle(gutColor, 0.85);
    for (const [ox, oy] of [
      [-30, -16], [30, -16], [-30, 16], [30, 16], [0, 0],
    ] as [number, number][]) {
      gut.fillCircle(cx + ox, gutY + oy, 26);
    }
    this.regionLabel(cx, gutY, "Intestines");
    this.hotspotZone(cx, gutY, 220, 150, "intestines");

    this.add
      .text(cx, top + 470, "Tap a glowing area to begin", {
        fontFamily: "Arial, sans-serif",
        fontSize: "24px",
        color: "#eaf6ff",
      })
      .setOrigin(0.5);
  }

  /** Pulsing additive glow behind a region marker. */
  private pulseGlow(x: number, y: number, tint: number): void {
    const glow = this.add
      .sprite(x, y, "soft")
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScale(1.6)
      .setAlpha(0.5);
    this.tweens.add({
      targets: glow,
      alpha: { from: 0.35, to: 0.7 },
      scale: { from: 1.4, to: 1.9 },
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  private regionLabel(x: number, y: number, text: string): void {
    this.add
      .text(x, y + 60, text, {
        fontFamily: "Arial, sans-serif",
        fontSize: "20px",
        color: "#eaf6ff",
        fontStyle: "bold",
        stroke: "#0a1530",
        strokeThickness: 4,
      })
      .setOrigin(0.5);
  }

  private hotspotZone(x: number, y: number, w: number, h: number, regionId: string): void {
    this.add
      .zone(x, y, w, h)
      .setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => {
        AudioFx.unlock();
        this.startMission(regionId);
      });
  }

  private startMission(regionId: string): void {
    this.cameras.main.fadeOut(350, 5, 12, 26);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.start("Game", { regionId });
    });
  }

  // ── Overlays ────────────────────────────────────────────────────────────────

  private showDisclaimer(): void {
    const { panel, depth } = this.makePanel();
    const created: Phaser.GameObjects.GameObject[] = [...panel];
    created.push(
      this.add
        .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 150, "Before you play", {
          fontFamily: "Arial, sans-serif",
          fontSize: "34px",
          color: "#eaf6ff",
          fontStyle: "bold",
        })
        .setOrigin(0.5)
        .setDepth(depth),
      this.add
        .text(
          GAME_WIDTH / 2,
          GAME_HEIGHT / 2 - 40,
          "This game is for entertainment. It is not\nmedical advice. If you're feeling unwell,\nplease talk to a healthcare provider.",
          {
            fontFamily: "Arial, sans-serif",
            fontSize: "24px",
            color: "#cfe8ff",
            align: "center",
            lineSpacing: 8,
          },
        )
        .setOrigin(0.5)
        .setDepth(depth),
    );

    const btn = this.makeButton(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 130, "I understand", depth, () => {
      btn.destroy();
      created.forEach((o) => o.destroy());
      this.maybeShowCheckIn();
    });
  }

  private maybeShowCheckIn(): void {
    const today = new Date().toISOString().slice(0, 10);
    if (SaveState.get().lastCheckIn === today) return;

    const { panel, depth } = this.makePanel();
    const title = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 150, "How are you feeling today?", {
        fontFamily: "Arial, sans-serif",
        fontSize: "30px",
        color: "#eaf6ff",
        fontStyle: "bold",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(depth);

    const created: Phaser.GameObjects.GameObject[] = [...panel, title];
    CHECK_IN_OPTIONS.forEach((label, i) => {
      const btn = this.makeButton(
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2 - 50 + i * 86,
        label,
        depth,
        () => {
          SaveState.recordCheckIn(today);
          created.forEach((o) => o.destroy());
          // Every answer routes to the Lungs in this slice.
        },
      );
      created.push(btn);
    });
  }

  /**
   * Dim backdrop + an OPAQUE rounded card. Distinct depths (300 dim, 301 card)
   * guarantee the card fully covers the menu behind it; the returned `depth`
   * (302) is for the panel's text/content so it sits above the card.
   */
  private makePanel(): { panel: Phaser.GameObjects.GameObject[]; depth: number } {
    const dim = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x05080f, 0.85)
      .setDepth(300)
      .setInteractive(); // swallow taps behind the panel
    const card = this.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH - 80, 540, 0x12233f, 1)
      .setStrokeStyle(2, 0x9fd8ff, 0.6)
      .setDepth(301);
    return { panel: [dim, card], depth: 302 };
  }

  private makeButton(
    x: number,
    y: number,
    label: string,
    depth: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const bg = this.add
      .rectangle(0, 0, 320, 64, 0x1f4f7a, 1)
      .setStrokeStyle(2, 0x9fd8ff, 0.7)
      .setInteractive({ useHandCursor: true });
    const txt = this.add
      .text(0, 0, label, {
        fontFamily: "Arial, sans-serif",
        fontSize: "26px",
        color: "#eaf6ff",
      })
      .setOrigin(0.5);
    const container = this.add.container(x, y, [bg, txt]).setDepth(depth + 1);
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => {
      AudioFx.unlock();
      onClick();
    });
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OVER, () => bg.setFillStyle(0x2a6699));
    bg.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => bg.setFillStyle(0x1f4f7a));
    return container;
  }
}
